type Listener = () => void;

function isUpdater<T>(x: unknown): x is (prev: T | null) => T | null {
  return typeof x === 'function';
}

export class SessionStore<T> {
  #key: string;
  #cached: T | null;
  #listeners = new Set<Listener>();

  constructor(key: string) {
    this.#key = key;
    this.#cached = this.#readFromStorage();
  }

  read = (): T | null => this.#cached;

  write = (updater: T | null | ((prev: T | null) => T | null)): void => {
    const next = isUpdater<T>(updater) ? updater(this.#cached) : updater;
    this.#cached = next;
    if (next === null) {
      sessionStorage.removeItem(this.#key);
    } else {
      sessionStorage.setItem(this.#key, JSON.stringify(next));
    }
    for (const listener of this.#listeners) listener();
  };

  subscribe = (listener: Listener): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  #readFromStorage(): T | null {
    if (typeof window === 'undefined') return null;
    const raw = sessionStorage.getItem(this.#key);
    if (!raw) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed === null || typeof parsed !== 'object') return null;
      return parsed as T;
    } catch {
      return null;
    }
  }
}
